import React from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import {
  Security,
  Speed,
  Verified,
  Business,
  CheckCircle,
  Launch,
  CalendarToday
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ValidadoLogo from '../components/ValidadoLogo';
import usePageTitle from '../hooks/usePageTitle';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  usePageTitle('Professional Background Verification Platform');

  const features = [
    {
      icon: <Security sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Secure Verification',
      description: 'Advanced encryption and secure data handling ensure your background checks are processed with the highest level of security and privacy.'
    },
    {
      icon: <Speed sx={{ fontSize: 48, color: theme.palette.success.main }} />,
      title: 'Lightning Fast',
      description: 'Get comprehensive background check results in minutes, not days. Our streamlined process delivers accurate results when you need them.'
    },
    {
      icon: <Verified sx={{ fontSize: 48, color: theme.palette.info.main }} />,
      title: 'Verified Results',
      description: 'Multi-source verification ensures accuracy and completeness. Every check is thoroughly validated before delivery.'
    },
    {
      icon: <Business sx={{ fontSize: 48, color: theme.palette.warning.main }} />,
      title: 'Enterprise Ready',
      description: 'Built for businesses of all sizes with bulk processing, API integration, and comprehensive reporting capabilities.'
    }
  ];

  const stats = [
    { number: '1M+', label: 'Background Checks Processed' },
    { number: '100+', label: 'Companies Trust Us' },
    { number: '99.9%', label: 'Accuracy Rate' },
    { number: '24/7', label: 'Support Available' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Box mb={4}>
                <ValidadoLogo width={300} height={120} />
              </Box>
              
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3
                }}
              >
                The Future of Background Verification
              </Typography>
              
              <Typography
                variant="h5"
                color="text.secondary"
                paragraph
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                Secure, fast, and reliable background checks for Latin America, USA, and Canada. 
                Empowering businesses across the Americas to make informed hiring decisions with confidence.
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 3
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Launch sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Official Launch
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <CalendarToday sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      Coming Soon
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Be among the first to experience the next generation of background verification
                  </Typography>
                </Paper>
              </Box>
              
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register-enhanced')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Get Early Access
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                {/* Hero Illustration */}
                <svg
                  width="400"
                  height="400"
                  viewBox="0 0 400 400"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Background Circle */}
                  <circle
                    cx="200"
                    cy="200"
                    r="180"
                    fill={alpha(theme.palette.primary.main, 0.1)}
                    stroke={theme.palette.primary.main}
                    strokeWidth="2"
                    strokeDasharray="10,5"
                  />
                  
                  {/* Shield */}
                  <path
                    d="M200 80L160 100V160C160 200 180 240 200 260C220 240 240 200 240 160V100L200 80Z"
                    fill={theme.palette.primary.main}
                    stroke={theme.palette.primary.dark}
                    strokeWidth="2"
                  />
                  
                  {/* Checkmark */}
                  <path
                    d="M180 160L195 175L220 140"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  
                  {/* Documents */}
                  <rect
                    x="120"
                    y="280"
                    width="60"
                    height="80"
                    rx="4"
                    fill={theme.palette.secondary.main}
                    stroke={theme.palette.secondary.dark}
                    strokeWidth="2"
                  />
                  <rect
                    x="220"
                    y="280"
                    width="60"
                    height="80"
                    rx="4"
                    fill={theme.palette.success.main}
                    stroke={theme.palette.success.dark}
                    strokeWidth="2"
                  />
                  
                  {/* Document Lines */}
                  <line x1="130" y1="300" x2="170" y2="300" stroke="white" strokeWidth="2" />
                  <line x1="130" y1="310" x2="160" y2="310" stroke="white" strokeWidth="2" />
                  <line x1="130" y1="320" x2="165" y2="320" stroke="white" strokeWidth="2" />
                  
                  <line x1="230" y1="300" x2="270" y2="300" stroke="white" strokeWidth="2" />
                  <line x1="230" y1="310" x2="260" y2="310" stroke="white" strokeWidth="2" />
                  <line x1="230" y1="320" x2="265" y2="320" stroke="white" strokeWidth="2" />
                  
                  {/* Floating Elements */}
                  <circle cx="100" cy="150" r="8" fill={theme.palette.warning.main} opacity="0.8" />
                  <circle cx="320" cy="180" r="6" fill={theme.palette.info.main} opacity="0.8" />
                  <circle cx="80" cy="250" r="5" fill={theme.palette.error.main} opacity="0.8" />
                  <circle cx="340" cy="120" r="7" fill={theme.palette.success.main} opacity="0.8" />
                </svg>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {stats.map((stat, index) => (
            <Box key={index} sx={{ flex: { xs: '1 1 45%', md: '1 1 20%' }, textAlign: 'center' }}>
              <Box textAlign="center">
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  color="primary"
                  gutterBottom
                >
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5), py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Why Choose Valida.do?
            </Typography>
            <Typography variant="h6" color="text.secondary" maxWidth="600px" mx="auto">
              Built with cutting-edge technology and designed for the modern workplace
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box display="flex" alignItems="flex-start" gap={3}>
                      <Box sx={{ flexShrink: 0 }}>
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" lineHeight={1.6}>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Coming Soon Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center">
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Coming Soon
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph maxWidth="800px" mx="auto">
            We're putting the finishing touches on the most advanced background verification platform for the Americas. 
            Specialized coverage for Latin America, USA, and Canada with local expertise and global standards.
          </Typography>
          
          <Box mt={4} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Chip
              icon={<CheckCircle />}
              label="Americas Coverage"
              color="primary"
              variant="outlined"
              size="medium"
            />
            <Chip
              icon={<CheckCircle />}
              label="Local Expertise"
              color="primary"
              variant="outlined"
              size="medium"
            />
            <Chip
              icon={<CheckCircle />}
              label="Multi-language Support"
              color="primary"
              variant="outlined"
              size="medium"
            />
            <Chip
              icon={<CheckCircle />}
              label="Enterprise APIs"
              color="primary"
              variant="outlined"
              size="medium"
            />
          </Box>
          
          <Box mt={6}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register-enhanced')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                }
              }}
            >
              Request Early Access
            </Button>
          </Box>
        </Box>
      </Container>
      
      {/* Footer */}
      <Box
        sx={{
          bgcolor: theme.palette.grey[900],
          color: 'white',
          py: 4,
          mt: 8
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <ValidadoLogo width={150} height={60} />
              </Box>
              <Typography variant="body2" color="grey.400">
                The future of background verification in the Americas. Coming Soon.
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box textAlign={{ xs: 'left', md: 'right' }}>
                <Typography variant="body2" color="grey.400" gutterBottom>
                  Ready to transform your hiring process?
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/register-enhanced')}
                  sx={{
                    borderColor: 'grey.600',
                    color: 'grey.300',
                    '&:hover': {
                      borderColor: 'grey.400',
                      color: 'white'
                    }
                  }}
                >
                  Join Waitlist
                </Button>
              </Box>
            </Box>
          </Box>
          
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.grey[700]}`,
              mt: 4,
              pt: 4
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Button
                color="inherit"
                onClick={() => navigate('/compliance')}
                sx={{
                  color: 'grey.400',
                  '&:hover': {
                    color: 'grey.200'
                  }
                }}
              >
                Legal Compliance
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate('/register-enhanced')}
                sx={{
                  color: 'grey.400',
                  '&:hover': {
                    color: 'grey.200'
                  }
                }}
              >
                Get Started
              </Button>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="grey.500">
                © 2025 Valida.do. All rights reserved. | Revolutionizing background verification since 2020.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;